// Power page: GregTech machines (LSC, Deep Earth Heating Pumps, turbines)
// read by the Butler (0.5.0 and later) and the rules it switches them by.
// Wire format: docs/protocol.md, "Power control".
import { normalizeStack, stackKey, type Stack } from "./stack.js";

export const POWER_BUTLER = "0.5.0";
/** First Butler that reads tanks and guards from one. */
export const TANK_BUTLER = "0.5.1";

export const POWER_MODES = ["manual", "auto", "on", "off"] as const;
export type PowerMode = (typeof POWER_MODES)[number];
export type MachineKind = "lsc" | "deph" | "turbine" | "tank" | "machine";

/** Default guard for a Deep Earth Heating Pump: it explodes when its distilled water runs out. */
export const DEFAULT_GUARD: PowerGuard = { stack: { type: "fluid", name: "ic2distilledwater" }, min: 100_000 };

/** One machine as the Butler reports it in power.machines. */
export interface WireMachine {
  id: string;
  node?: string;
  address: string;
  name?: string;
  kind: MachineKind;
  allowed?: boolean;
  active?: boolean;
  hasWork?: boolean;
  progress?: number;
  maxProgress?: number;
  stored?: number;
  capacity?: number;
  /** Tank kind only: what getTankInfo returned, in litres. */
  tanks?: Array<{ name?: string; label?: string; amount: number; capacity?: number }>;
  sensor: string[];
  mode?: PowerMode;
  desired?: boolean;
  reason?: string;
  guard?: { key: string; amount?: number; min: number; tank?: string; tripped: boolean };
  lastSwitch?: { ago: number; allowed: boolean; reason?: string };
  error?: string;
  stale?: boolean;
}

export interface WireControl {
  enabled: boolean;
  lsc?: string;
  fill?: number;
  state: "charging" | "full" | "holding";
  low?: number;
  high?: number;
  relays?: boolean;
}

export interface PowerGuard {
  stack: Stack;
  min: number;
  /** Machine id of a tank to read instead of AE (a fluid stack only). */
  tank?: string;
}

/** What the app keeps per machine (power.set carries mode and guard). */
export interface MachineSettings {
  id: string;
  label: string | null;
  mode: PowerMode;
  guard: PowerGuard | null;
  /** Not drawn on the Power page; the Butler still reads and switches it. */
  hidden?: boolean;
}

export interface PowerConfig {
  /** Master switch: off means the Butler reads and reports but switches nothing. */
  enabled: boolean;
  /** LSC fill (0 to 1) at or under which auto machines are switched on. */
  low: number;
  /** LSC fill at or over which auto machines are switched off. */
  high: number;
  /** Machine id of the LSC the rules follow; null picks the first one found. */
  lsc: string | null;
  machines: MachineSettings[];
}

export const DEFAULT_POWER_CONFIG: PowerConfig = { enabled: false, low: 0.3, high: 0.9, lsc: null, machines: [] };

/** Figures read out of the sensor lines. Absent when the line wasn't there. */
export interface SensorFacts {
  /** Turbine output, EU/t. */
  output?: number;
  running?: boolean;
  /** Turbine speed or multiblock efficiency, %. */
  efficiency?: number;
  /** Rotor damage, %. */
  damage?: number;
  /** Optimal flow, L per tick or second (flowUnit). */
  flow?: number;
  flowUnit?: "t" | "s";
  fuel?: number;
  maintenanceOk?: boolean;
  problems?: number;
  /** LSC: average EU/t in and out over 5 s, 5 min and 1 h, last tick, passive loss. */
  avgIn?: number;
  avgOut?: number;
  avgIn5m?: number;
  avgOut5m?: number;
  avgIn1h?: number;
  avgOut1h?: number;
  euIn?: number;
  euOut?: number;
  passiveLoss?: number;
  usedPct?: number;
  timeToFull?: string;
  timeToEmpty?: string;
  wireless?: boolean;
  /** Dynamo or controller buffer: [stored, capacity]. */
  buffer?: [number, number];
}

export interface MachineView extends WireMachine {
  label: string | null;
  settings: MachineSettings;
  facts: SensorFacts;
  /** The sensor lines decoded to English. */
  lines: string[];
}

export interface PowerStatus {
  /** When the Butler last reported its machines; null when it never did. */
  at: number | null;
  connected: boolean;
  /** False when the connected Butler is older than POWER_BUTLER. */
  supported: boolean;
  butlerVersion: string | null;
  machines: MachineView[];
  control: WireControl | null;
  config: PowerConfig;
  /** The LSC the rules follow, with its net rate from the stored-EU samples. */
  lsc: { id: string; stored: number; capacity: number; netPerTick: number | null; secondsToFull: number | null; secondsToEmpty: number | null } | null;
}

export interface PowerHistory {
  id: string | null;
  from: number;
  to: number;
  /** [time, stored EU, capacity EU], oldest first. */
  points: Array<[number, number, number]>;
}

// ------------------------------------------------------------- sensor lines

/** English text of the GregTech keys the sensor lines may carry (en_US.lang). */
const TEMPLATES: Record<string, string> = {
  "kekztech.infodata.operational_data": "Operational Data:",
  "kekztech.infodata.lapotronic_super_capacitor.eu_stored": "EU Stored: %s EU",
  "kekztech.infodata.lapotronic_super_capacitor.used_capacity": "Used Capacity: %s",
  "kekztech.infodata.lapotronic_super_capacitor.total_capacity": "Total Capacity: %s EU",
  "kekztech.infodata.lapotronic_super_capacitor.passive_loss": "Passive Loss: %s EU/t",
  "kekztech.infodata.lapotronic_super_capacitor.eu_in": "EU IN: %s EU/t",
  "kekztech.infodata.lapotronic_super_capacitor.eu_out": "EU OUT: %s EU/t",
  "kekztech.infodata.lapotronic_super_capacitor.avg_eu_in.sec": "Avg EU IN: %s (last %s seconds)",
  "kekztech.infodata.lapotronic_super_capacitor.avg_eu_out.sec": "Avg EU OUT: %s (last %s seconds)",
  "kekztech.infodata.lapotronic_super_capacitor.avg_eu_in.min5": "Avg EU IN: %s (last 5 minutes)",
  "kekztech.infodata.lapotronic_super_capacitor.avg_eu_out.min5": "Avg EU OUT: %s (last 5 minutes)",
  "kekztech.infodata.lapotronic_super_capacitor.avg_eu_in.hour1": "Avg EU IN: %s (last 1 hour)",
  "kekztech.infodata.lapotronic_super_capacitor.avg_eu_out.hour1": "Avg EU OUT: %s (last 1 hour)",
  "kekztech.infodata.lapotronic_super_capacitor.time_to.full": "Time to Full: %s",
  "kekztech.infodata.lapotronic_super_capacitor.time_to.empty": "Time to Empty: %s",
  "kekztech.infodata.lapotronic_super_capacitor.time_to.sth": "Time to Something: Infinite years",
  "kekztech.infodata.lapotronic_super_capacitor.wireless_mode": "Wireless mode: %s",
  "kekztech.infodata.lapotronic_super_capacitor.wireless_mode.enabled": "Wireless mode: enabled",
  "kekztech.infodata.lapotronic_super_capacitor.wireless_mode.disabled": "Wireless mode: disabled",
  "kekztech.infodata.lapotronic_super_capacitor.capacitors": "%s Capacitors detected: %s",
  "kekztech.infodata.lapotronic_super_capacitor.wireless_eu": "Total wireless EU: %s EU",
  "kekztech.infodata.multi.maintenance_status": "Maintenance Status: %s",
  "kekztech.infodata.multi.maintenance_status.ok": "Maintenance Status: Working perfectly",
  "kekztech.infodata.multi.maintenance_status.bad": "Maintenance Status: Has Problems",
  "GT5U.infodata.energy": "Stored Energy: %s EU / %s EU",
  "GT5U.infodata.turbine.output": "%s: %s EU/t",
  "GT5U.infodata.turbine.efficiency": "Current Speed: %s %",
  "GT5U.infodata.turbine.flow": "Optimal Flow: %s L/%s (%s)",
  "GT5U.infodata.turbine.fuel": "Fuel Remaining: %s L",
  "GT5U.infodata.turbine.dmg": "Turbine Damage: %s %",
  "GT5U.infodata.turbine.pollution": "Pollution reduced to: %s %",
  "GT5U.multiblock.recipesDone.fmt": "Recipes Finished: %s",
  "GT5U.turbine.running.true": "Turbine running",
  "GT5U.turbine.running.false": "Turbine stopped",
  "GT5U.turbine.maintenance.false": "No Maintenance issues",
  "GT5U.turbine.maintenance.true": "Needs Maintenance",
};

/** Turns one sensor line into English: GregTech's "key\\arg\\arg" form (recent versions) or text as is. */
export function decodeSensorLine(line: string): string {
  const parts = line.split("\\\\").map((p) => p.replace(/^\u0001/, "").replace(/§./g, ""));
  const key = parts[0];
  const args = parts.slice(1);
  const template = TEMPLATES[key];
  if (template) {
    let i = 0;
    return template.replace(/%s/g, () => args[i++] ?? "?");
  }
  if (/^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)+$/.test(key)) {
    // A key this app has no text for: keep it readable.
    const tail = key.split(".").slice(-2).join(" ").replace(/_/g, " ");
    return args.length ? `${tail}: ${args.join(" ")}` : tail;
  }
  return parts.join(" ");
}

/** "1,234,567" or "1 234.5" to a number; null when there is none. */
export function sensorNumber(text: string | undefined): number | null {
  if (text === undefined) return null;
  const n = Number(text.replace(/[,\s_]/g, ""));
  return Number.isFinite(n) ? n : null;
}

/** Reads the useful figures out of decoded sensor lines. */
export function parseSensor(lines: string[]): SensorFacts {
  const f: SensorFacts = {};
  const num = (v: string | undefined) => sensorNumber(v) ?? undefined;
  const NUM = "(-?\\d[\\d,.\\s]*)";
  for (const line of lines) {
    let m: RegExpExecArray | null;
    if ((m = new RegExp(`^Avg EU (IN|OUT): ${NUM}\\s*\\(last (\\d+) (seconds|minutes|hour)\\)`, "i").exec(line))) {
      const v = num(m[2]);
      const which = m[4].toLowerCase().startsWith("second") ? "" : m[4].toLowerCase().startsWith("minute") ? "5m" : "1h";
      const field = `avg${m[1].toUpperCase() === "IN" ? "In" : "Out"}${which}` as "avgIn" | "avgOut" | "avgIn5m" | "avgOut5m" | "avgIn1h" | "avgOut1h";
      if (v !== undefined && f[field] === undefined) f[field] = v;
    } else if ((m = new RegExp(`^EU (IN|OUT): ${NUM}`, "i").exec(line))) {
      const v = num(m[2]);
      if (m[1].toUpperCase() === "IN") f.euIn ??= v;
      else f.euOut ??= v;
    } else if ((m = new RegExp(`^Passive Loss: ${NUM}`, "i").exec(line))) {
      f.passiveLoss ??= num(m[1]);
    } else if ((m = /^Used Capacity: ([\d.,]+)\s*%/i.exec(line))) {
      f.usedPct ??= num(m[1]);
    } else if ((m = /^Time to Full: (.+)$/i.exec(line))) {
      f.timeToFull = m[1].trim();
    } else if ((m = /^Time to Empty: (.+)$/i.exec(line))) {
      f.timeToEmpty = m[1].trim();
    } else if ((m = /^Wireless mode: (\w+)/i.exec(line))) {
      f.wireless = m[1].toLowerCase() === "enabled";
    } else if ((m = /^Maintenance Status: (.+)$/i.exec(line))) {
      f.maintenanceOk = /working perfectly/i.test(m[1]);
    } else if (/^No Maintenance issues/i.test(line)) {
      f.maintenanceOk = true;
    } else if (/^Needs Maintenance/i.test(line)) {
      f.maintenanceOk = false;
    } else if ((m = new RegExp(`^Stored Energy: ${NUM}\\s*EU\\s*/\\s*${NUM}`, "i").exec(line))) {
      const a = num(m[1]);
      const b = num(m[2]);
      if (a !== undefined && b !== undefined) f.buffer = [a, b];
    } else if ((m = new RegExp(`^(?:Current Speed|Efficiency|Turbine Efficiency):?\\s*${NUM}\\s*%`, "i").exec(line))) {
      f.efficiency ??= num(m[1]);
    } else if ((m = new RegExp(`^Optimal Flow: ${NUM}\\s*L/(t|s)`, "i").exec(line))) {
      f.flow = num(m[1]);
      f.flowUnit = m[2] as "t" | "s";
    } else if ((m = new RegExp(`^Fuel Remaining: ${NUM}`, "i").exec(line))) {
      f.fuel = num(m[1]);
    } else if ((m = new RegExp(`^(?:Current )?Turbine Damage: ${NUM}`, "i").exec(line))) {
      f.damage = num(m[1]);
    } else if ((m = new RegExp(`^(.*?):\\s*${NUM}\\s*EU/t\\s*$`, "i").exec(line)) && /turbine/i.test(m[1])) {
      f.output = num(m[2]);
      f.running = !/stopped/i.test(m[1]);
    } else {
      // Generic multiblock lines: "Problems: 0 Efficiency: 100.0 %".
      if ((m = /Problems:\s*(\d+)/i.exec(line))) f.problems = Number(m[1]);
      if ((m = new RegExp(`Efficiency:\\s*${NUM}\\s*%`, "i").exec(line))) f.efficiency ??= num(m[1]);
    }
  }
  return f;
}

// ------------------------------------------------------------- settings

export function isPowerMode(v: unknown): v is PowerMode {
  return typeof v === "string" && (POWER_MODES as readonly string[]).includes(v);
}

/** Checks a guard from the API; throws a message on bad input. */
export function normalizeGuard(raw: unknown): PowerGuard | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "object") throw new Error("guard must be an object or null");
  const g = raw as { stack?: unknown; min?: unknown; tank?: unknown };
  const stack = normalizeStack(g.stack);
  if (!stack) throw new Error("guard needs a valid stack");
  const min = Number(g.min);
  if (!Number.isFinite(min) || min < 0) throw new Error("guard min must be a number >= 0");
  if (g.tank === undefined || g.tank === null || g.tank === "") return { stack, min };
  if (typeof g.tank !== "string") throw new Error("guard tank must be a machine id");
  if (stack.type !== "fluid") throw new Error("a tank guard needs a fluid");
  return { stack, min, tank: g.tank };
}

/** The power.set command for a config. */
export function powerSetCommand(config: PowerConfig): {
  t: "power.set";
  enabled: boolean;
  low: number;
  high: number;
  lsc?: string;
  machines: Array<{ id: string; mode: PowerMode; guard?: PowerGuard }>;
} {
  return {
    t: "power.set",
    enabled: config.enabled,
    low: config.low,
    high: config.high,
    ...(config.lsc ? { lsc: config.lsc } : {}),
    machines: config.machines
      .filter((m) => m.mode !== "manual" || m.guard)
      .map((m) => ({ id: m.id, mode: m.mode, ...(m.guard ? { guard: m.guard } : {}) })),
  };
}

export function guardKey(g: PowerGuard): string {
  return stackKey(g.stack);
}

/** What a kind is called on the page. */
export function kindLabel(kind: MachineKind): string {
  return { lsc: "Lapotronic Supercapacitor", deph: "Deep Earth Heating Pump", turbine: "Turbine", tank: "Tank", machine: "Machine" }[kind];
}
